import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  X, Plus, Check, Circle, Trash2, Calendar, Users, User, 
  Loader2, CheckCircle2, Clock, Edit3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TodoTasks({ onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newShared, setNewShared] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const loadTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data.tasks || []);
    } catch (err) {
      console.error('Load tasks error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const addTask = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await axios.post(`${API}/tasks`, {
        title: newTitle.trim(),
        description: newDesc.trim(),
        due_date: newDueDate,
        shared: newShared,
      });
      setTasks([res.data, ...tasks]);
      setNewTitle('');
      setNewDesc('');
      setNewDueDate('');
      setNewShared(false);
      setShowAddForm(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add task');
    }
  };

  const toggleStatus = async (task) => {
    const newStatus = task.status === 'done' ? 'pending' : 'done';
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      setTasks(tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      setTasks(tasks.filter(t => t.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveEdit = async (task) => {
    if (!editTitle.trim()) return;
    try {
      await axios.put(`${API}/tasks/${task.id}`, { title: editTitle.trim() });
      setTasks(tasks.map(t => t.id === task.id ? { ...t, title: editTitle.trim() } : t));
      setEditingId(null);
    } catch (err) {
      console.error('Edit error:', err);
    }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'pending') return t.status === 'pending';
    if (filter === 'done') return t.status === 'done';
    if (filter === 'shared') return t.shared;
    return true;
  });

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const doneCount = tasks.filter(t => t.status === 'done').length;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="todo-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-[#1B3A6B]">Tasks</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="todo-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          
          {/* Stats + Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-xs text-[#94A3B8] mr-2">
              <Clock className="w-3.5 h-3.5" /> {pendingCount} pending
              <span className="mx-1">|</span>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> {doneCount} done
            </div>
            <div className="flex gap-1 ml-auto">
              {['all', 'pending', 'done', 'shared'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                    filter === f ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
                  }`}
                  data-testid={`todo-filter-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B] mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-[#94A3B8] py-12">
              {filter === 'all' ? 'No tasks yet. Add one below!' : `No ${filter} tasks.`}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map(task => (
                <div
                  key={task.id}
                  className={`group flex items-start gap-3 p-3 rounded-xl border transition-all ${
                    task.status === 'done'
                      ? 'bg-[#F9F8F6] border-[#E2DFD8]'
                      : 'bg-white border-[#E2DFD8] hover:border-[#1B3A6B]/20 hover:shadow-sm'
                  }`}
                  data-testid={`task-item-${task.id}`}
                >
                  <button
                    onClick={() => toggleStatus(task)}
                    className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      task.status === 'done'
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-[#D1CBC0] hover:border-[#1B3A6B]'
                    }`}
                    data-testid={`task-toggle-${task.id}`}
                  >
                    {task.status === 'done' && <Check className="w-3 h-3" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    {editingId === task.id ? (
                      <div className="flex gap-2">
                        <input
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEdit(task)}
                          className="flex-1 px-2 py-1 text-sm border border-[#E2DFD8] rounded"
                          autoFocus
                        />
                        <button onClick={() => saveEdit(task)} className="text-xs text-[#1B3A6B] font-medium">Save</button>
                        <button onClick={() => setEditingId(null)} className="text-xs text-[#94A3B8]">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <p className={`text-sm leading-tight ${task.status === 'done' ? 'line-through text-[#94A3B8]' : 'text-[#0F1A2A]'}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-[#94A3B8] mt-0.5">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {task.due_date && (
                            <span className="flex items-center gap-1 text-[10px] text-[#B8860B]">
                              <Calendar className="w-3 h-3" /> {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {task.shared && (
                            <span className="flex items-center gap-1 text-[10px] text-[#1B3A6B]">
                              <Users className="w-3 h-3" /> Shared
                            </span>
                          )}
                          {!task.shared && (
                            <span className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
                              <User className="w-3 h-3" /> Personal
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setEditingId(task.id); setEditTitle(task.title); }} className="p-1 hover:bg-[#F4F2EC] rounded">
                      <Edit3 className="w-3.5 h-3.5 text-[#475569]" />
                    </button>
                    <button onClick={() => deleteTask(task.id)} className="p-1 hover:bg-red-50 rounded">
                      <Trash2 className="w-3.5 h-3.5 text-[#475569] hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Task */}
        <div className="p-4 border-t border-[#E2DFD8]">
          {showAddForm ? (
            <div className="space-y-2" data-testid="add-task-form">
              <input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTask()}
                placeholder="Task title..."
                className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                data-testid="task-title-input"
                autoFocus
              />
              <textarea
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] resize-none"
                data-testid="task-desc-input"
              />
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={newDueDate}
                  onChange={e => setNewDueDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                  data-testid="task-due-date-input"
                />
                <label className="flex items-center gap-1.5 text-xs text-[#475569] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newShared}
                    onChange={e => setNewShared(e.target.checked)}
                    className="rounded border-[#D1CBC0]"
                    data-testid="task-shared-checkbox"
                  />
                  Share with team
                </label>
                <div className="ml-auto flex gap-2">
                  <button onClick={() => setShowAddForm(false)} className="text-xs text-[#94A3B8]">Cancel</button>
                  <button
                    onClick={addTask}
                    disabled={!newTitle.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]"
                    data-testid="task-save-btn"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-[#E2DFD8] rounded-lg text-sm text-[#475569] hover:border-[#1B3A6B] hover:text-[#1B3A6B] transition-colors"
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4" /> Add Task
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
